import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Phone, Mail, Star } from 'lucide-react';
import api from '@/lib/api';

export default function WaitlistManagement() {
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    preferred_services: [],
    preferred_days_times: '',
    staff_preference: '',
  });

  useEffect(() => {
    loadWaitlist();
  }, []);

  const loadWaitlist = async () => {
    try {
      const response = await api.get('/waitlist');
      setWaitlist(response.data);
    } catch (error) {
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await api.put(`/waitlist/${editingCustomer.id}`, formData);
        toast.success('Customer updated successfully');
      } else {
        await api.post('/waitlist', formData);
        toast.success('Customer added to waitlist');
      }
      setDialogOpen(false);
      setEditingCustomer(null);
      setFormData({ name: '', phone: '', email: '', preferred_services: [], preferred_days_times: '', staff_preference: '' });
      loadWaitlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      phone: customer.phone,
      email: customer.email,
      preferred_services: customer.preferred_services || [],
      preferred_days_times: customer.preferred_days_times || '',
      staff_preference: customer.staff_preference || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this customer from the waitlist?')) return;
    try {
      await api.delete(`/waitlist/${id}`);
      toast.success('Customer removed from waitlist');
      loadWaitlist();
    } catch (error) {
      toast.error('Failed to remove customer');
    }
  };

  return (
    <Layout active="waitlist">
      <div data-testid="waitlist-page" className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
              Waitlist Management
            </h1>
            <p className="text-gray-600 mt-2">Customers ready to fill cancelled slots</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-waitlist-button" onClick={() => {
                setEditingCustomer(null);
                setFormData({ name: '', phone: '', email: '', preferred_services: [], preferred_days_times: '', staff_preference: '' });
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Customer
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add Customer to Waitlist'}</DialogTitle>
                <DialogDescription>
                  {editingCustomer ? 'Update customer details' : 'Add a new customer to the waitlist'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    data-testid="customer-name-input"
                    placeholder="Jane Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      data-testid="customer-phone-input"
                      placeholder="+1234567890"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      data-testid="customer-email-input"
                      type="email"
                      placeholder="jane@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred_days_times">Preferred Days/Times (optional)</Label>
                  <Input
                    id="preferred_days_times"
                    data-testid="customer-preferences-input"
                    placeholder="Weekday mornings, Tuesday afternoons"
                    value={formData.preferred_days_times}
                    onChange={(e) => setFormData({ ...formData, preferred_days_times: e.target.value })}
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="waitlist-form-submit">
                    {editingCustomer ? 'Update' : 'Add'} Customer
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700"></div>
          </div>
        ) : waitlist.length === 0 ? (
          <Card className="bg-white border-border shadow-sm">
            <CardContent className="text-center py-12">
              <p className="text-gray-500 mb-4">No customers on the waitlist yet</p>
              <Button onClick={() => setDialogOpen(true)} data-testid="empty-add-customer-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Customer
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {waitlist.map((customer) => (
              <Card key={customer.id} className="bg-white border-border shadow-sm" data-testid={`waitlist-card-${customer.id}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold">{customer.name}</h3>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          {customer.responsiveness_score}
                        </Badge>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="h-4 w-4" />
                          {customer.phone}
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail className="h-4 w-4" />
                          {customer.email}
                        </div>
                      </div>
                      {customer.preferred_days_times && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Prefers:</span> {customer.preferred_days_times}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(customer)}
                        data-testid={`edit-customer-${customer.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(customer.id)}
                        data-testid={`delete-customer-${customer.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
